import { useState, memo } from 'react';
import { useMarket } from '@contexts/MarketContext';
import { cn } from '@lib/utils';

/**
 * Cash Flow Report Component (Fluxo de Caixa)
 * @task B.INV.9 - Cash Flow Report with OPEX/CAPEX/Financing structure
 *
 * Displays detailed cash flow breakdown with:
 * - Atividades Operacionais (OPEX)
 * - Atividades de Investimento (CAPEX)
 * - Atividades de Financiamento
 * - Monthly columns with expandable rows
 */

interface CashFlowSection {
  id: string;
  label: string;
  labelBR: string;
  type: 'header' | 'income' | 'expense';
  color: string;
  items: CashFlowItem[];
}

interface CashFlowItem {
  id: string;
  label: string;
  labelBR: string;
  type: 'income' | 'expense';
  months: number[];
}

interface CashFlowReportProps {
  className?: string;
}

// Demo data for Cash Flow Report
const cashFlowData: CashFlowSection[] = [
  {
    id: 'opex',
    label: 'Operating Activities (OPEX)',
    labelBR: 'Atividades Operacionais (OPEX)',
    type: 'header',
    color: 'orange',
    items: [
      { id: 'recebimento', label: 'Customer Receipts', labelBR: 'Recebimento de Clientes', type: 'income', months: [125000, 142000, 138000, 165000, 189000, 210000] },
      { id: 'novas-vendas', label: 'New Sales', labelBR: 'Novas Vendas', type: 'income', months: [45000, 52000, 48000, 62000, 71000, 78000] },
      { id: 'salarios', label: 'Salaries & Wages', labelBR: 'Salários e Ordenados', type: 'expense', months: [-35000, -35000, -36000, -36000, -38000, -40000] },
      { id: 'impostos', label: 'Tax on Revenue', labelBR: 'Impostos sobre Faturamento', type: 'expense', months: [-8500, -9700, -9300, -11350, -13000, -14400] },
      { id: 'despesas-adm', label: 'Admin Expenses', labelBR: 'Despesas Administrativas', type: 'expense', months: [-12000, -11500, -12500, -13000, -12800, -14000] },
      { id: 'despesas-marketing', label: 'Marketing Expenses', labelBR: 'Despesas de Marketing', type: 'expense', months: [-8000, -9500, -7500, -11000, -15000, -12000] },
      { id: 'aluguel', label: 'Rent', labelBR: 'Aluguel', type: 'expense', months: [-5000, -5000, -5000, -5000, -5000, -5000] },
      { id: 'tecnologia', label: 'Technology', labelBR: 'Despesas de Tecnologia', type: 'expense', months: [-3500, -3500, -4000, -4000, -4500, -5000] },
      { id: 'rh', label: 'HR Expenses', labelBR: 'Despesas de RH', type: 'expense', months: [-2000, -2200, -2100, -2500, -2700, -3000] },
    ],
  },
  {
    id: 'capex',
    label: 'Investment Activities (CAPEX)',
    labelBR: 'Atividades de Investimento (CAPEX)',
    type: 'header',
    color: 'purple',
    items: [
      { id: 'maquinas', label: 'Machinery & Equipment', labelBR: 'Máquinas e Equipamentos', type: 'expense', months: [-15000, 0, -8000, 0, -25000, 0] },
      { id: 'obra', label: 'Construction', labelBR: 'Obra', type: 'expense', months: [0, 0, 0, -50000, -30000, 0] },
      { id: 'estrutura', label: 'Infrastructure', labelBR: 'Estrutura', type: 'expense', months: [-5000, -3000, 0, -2000, -4000, -3000] },
      { id: 'novo-produto', label: 'New Product Development', labelBR: 'Desenvolvimento Novo Produto', type: 'expense', months: [-10000, -12000, -8000, -15000, -10000, -8000] },
    ],
  },
  {
    id: 'financing',
    label: 'Financing Activities',
    labelBR: 'Atividades de Financiamento',
    type: 'header',
    color: 'green',
    items: [
      { id: 'despesas-financeiras', label: 'Financial Expenses', labelBR: 'Despesas Financeiras', type: 'expense', months: [-2500, -2400, -2300, -2200, -2100, -2000] },
      { id: 'receitas-financeiras', label: 'Financial Income', labelBR: 'Receitas Financeiras', type: 'income', months: [1200, 1350, 1500, 1650, 1800, 2000] },
      { id: 'entrada-emprestimos', label: 'Loan Receipts', labelBR: 'Entrada de Empréstimos', type: 'income', months: [0, 0, 100000, 0, 0, 0] },
      { id: 'saida-emprestimos', label: 'Loan Payments', labelBR: 'Saída de Empréstimos', type: 'expense', months: [-5000, -5000, -5000, -5000, -5000, -5000] },
      { id: 'dividendos', label: 'Dividends', labelBR: 'Dividendos', type: 'expense', months: [0, 0, 0, -20000, 0, 0] },
    ],
  },
];

// Brazilian data (5x scale for BRL)
const cashFlowDataBR: CashFlowSection[] = cashFlowData.map(section => ({
  ...section,
  items: section.items.map(item => ({
    ...item,
    months: item.months.map(v => v * 5),
  })),
}));

const monthLabelsEN = ['Aug', 'Sep', 'Oct', 'Nov', 'Dec', 'Jan'];
const monthLabelsBR = ['Ago', 'Set', 'Out', 'Nov', 'Dez', 'Jan'];

export const CashFlowReport = memo(function CashFlowReport({ className }: CashFlowReportProps): JSX.Element {
  const { isBrazil, formatCurrency } = useMarket();
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    opex: true,
    capex: true,
    financing: true,
  });

  const data = isBrazil ? cashFlowDataBR : cashFlowData;
  const monthLabels = isBrazil ? monthLabelsBR : monthLabelsEN;

  const toggleSection = (sectionId: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  };

  // Calculate totals
  const calculateSectionTotal = (section: CashFlowSection, monthIndex: number): number => {
    return section.items.reduce((sum, item) => sum + (item.months[monthIndex] ?? 0), 0);
  };

  const calculateMonthlyResult = (monthIndex: number): number => {
    return data.reduce((sum, section) => sum + calculateSectionTotal(section, monthIndex), 0);
  };

  // Initial balance (demo)
  const initialBalance = isBrazil ? 500000 : 100000;

  // Calculate cumulative balance
  const cumulativeBalances = monthLabels.map((_, index) => {
    let balance = initialBalance;
    for (let i = 0; i <= index; i++) {
      balance += calculateMonthlyResult(i);
    }
    return balance;
  });

  const sectionColors = {
    orange: { bg: 'bg-orange-50', border: 'border-orange-400', text: 'text-orange-700' },
    purple: { bg: 'bg-purple-50', border: 'border-purple-400', text: 'text-purple-700' },
    green: { bg: 'bg-emerald-50', border: 'border-emerald-400', text: 'text-emerald-700' },
  } as const;

  return (
    <div className={cn('rounded-xl bg-white shadow-card overflow-hidden', className)}>
      {/* Header */}
      <div className="border-b border-gray-100 px-4 sm:px-6 py-4">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              {isBrazil ? 'Fluxo de Caixa' : 'Cash Flow Statement'}
            </h3>
            <p className="text-sm text-gray-500">
              {isBrazil ? 'Demonstrativo mensal de movimentações' : 'Monthly cash movements breakdown'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setExpandedSections({ opex: true, capex: true, financing: true })}
              className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50 transition-colors"
            >
              {isBrazil ? 'Expandir Todos' : 'Expand All'}
            </button>
            <button
              onClick={() => setExpandedSections({ opex: false, capex: false, financing: false })}
              className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50 transition-colors"
            >
              {isBrazil ? 'Recolher Todos' : 'Collapse All'}
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full min-w-[700px]">
          <thead>
            <tr className="border-b border-gray-100 bg-gray-50">
              <th className="sticky left-0 z-10 bg-gray-50 px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 min-w-[200px]">
                {isBrazil ? 'Descrição' : 'Description'}
              </th>
              {monthLabels.map((month, index) => (
                <th
                  key={month}
                  className={cn(
                    'px-3 py-3 text-right text-xs font-semibold uppercase tracking-wider text-gray-500 min-w-[100px]',
                    index === monthLabels.length - 1 && 'bg-finova-teal-50 text-finova-teal-700'
                  )}
                >
                  {month}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {/* Initial Balance - Shows opening balance for each month (previous month's closing balance) */}
            <tr className="bg-slate-100">
              <td className="sticky left-0 z-10 bg-slate-100 px-4 py-2.5 text-sm font-semibold text-slate-700">
                {isBrazil ? 'Saldo Inicial' : 'Opening Balance'}
              </td>
              {monthLabels.map((_, index) => {
                // Opening balance for each month = initial balance + sum of all previous months' results
                let openingBalance = initialBalance;
                for (let i = 0; i < index; i++) {
                  openingBalance += calculateMonthlyResult(i);
                }
                return (
                  <td key={index} className="px-3 py-2.5 text-right text-sm font-semibold text-slate-700">
                    {formatCurrency(openingBalance)}
                  </td>
                );
              })}
            </tr>

            {/* Sections */}
            {data.map((section) => {
              const colorKey = section.color as keyof typeof sectionColors;
              const colors = sectionColors[colorKey] ?? sectionColors['orange'];
              const isExpanded = expandedSections[section.id] ?? true;

              return (
                <SectionRows
                  key={section.id}
                  section={section}
                  colors={colors}
                  isExpanded={isExpanded}
                  onToggle={() => toggleSection(section.id)}
                  formatCurrency={formatCurrency}
                  isBrazil={isBrazil}
                  calculateSectionTotal={calculateSectionTotal}
                />
              );
            })}

            {/* Monthly Result */}
            <tr className="bg-finova-teal-50 border-t-2 border-finova-teal-200">
              <td className="sticky left-0 z-10 bg-finova-teal-50 px-4 py-3 text-sm font-bold text-finova-teal-800">
                {isBrazil ? 'Resultado do Mês' : 'Monthly Result'}
              </td>
              {monthLabels.map((_, index) => {
                const result = calculateMonthlyResult(index);
                return (
                  <td
                    key={index}
                    className={cn(
                      'px-3 py-3 text-right text-sm font-bold',
                      result >= 0 ? 'text-emerald-600' : 'text-rose-600'
                    )}
                  >
                    {result >= 0 ? '+' : ''}{formatCurrency(result)}
                  </td>
                );
              })}
            </tr>

            {/* Final Balance */}
            <tr className="bg-slate-800">
              <td className="sticky left-0 z-10 bg-slate-800 px-4 py-3 text-sm font-bold text-white">
                {isBrazil ? 'Saldo Final de Disponibilidade' : 'Closing Cash Balance'}
              </td>
              {cumulativeBalances.map((balance, index) => (
                <td
                  key={index}
                  className={cn(
                    'px-3 py-3 text-right text-sm font-bold',
                    balance >= 0 ? 'text-emerald-400' : 'text-rose-400'
                  )}
                >
                  {formatCurrency(balance)}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>

      {/* Summary Cards */}
      <div className="border-t border-gray-100 p-4 sm:p-6">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 sm:gap-4">
          <SummaryCard
            label={isBrazil ? 'Total Entradas' : 'Total Inflows'}
            value={data.reduce((sum, section) =>
              sum + section.items
                .filter(item => item.type === 'income')
                .reduce((itemSum, item) => itemSum + item.months.reduce((a, b) => a + b, 0), 0)
            , 0)}
            formatCurrency={formatCurrency}
            color="emerald"
            isBrazil={isBrazil}
          />
          <SummaryCard
            label={isBrazil ? 'Total Saídas' : 'Total Outflows'}
            value={Math.abs(data.reduce((sum, section) =>
              sum + section.items
                .filter(item => item.type === 'expense')
                .reduce((itemSum, item) => itemSum + item.months.reduce((a, b) => a + b, 0), 0)
            , 0))}
            formatCurrency={formatCurrency}
            color="rose"
            isNegative
            isBrazil={isBrazil}
          />
          <SummaryCard
            label={isBrazil ? 'Saldo Acumulado' : 'Net Cash Flow'}
            value={cumulativeBalances[cumulativeBalances.length - 1] ?? 0}
            formatCurrency={formatCurrency}
            color="teal"
            isBrazil={isBrazil}
          />
          <SummaryCard
            label={isBrazil ? 'Variação %' : 'Change %'}
            value={((cumulativeBalances[cumulativeBalances.length - 1] ?? 0) - initialBalance) / initialBalance * 100}
            formatCurrency={(v) => `${v >= 0 ? '+' : ''}${v.toFixed(1)}%`}
            color="violet"
            isBrazil={isBrazil}
          />
        </div>
      </div>
    </div>
  );
});

// Section Rows Component
interface SectionRowsProps {
  section: CashFlowSection;
  colors: { bg: string; border: string; text: string };
  isExpanded: boolean;
  onToggle: () => void;
  formatCurrency: (amount: number) => string;
  isBrazil: boolean;
  calculateSectionTotal: (section: CashFlowSection, monthIndex: number) => number;
}

const SectionRows = memo(function SectionRows({
  section,
  colors,
  isExpanded,
  onToggle,
  formatCurrency,
  isBrazil,
  calculateSectionTotal,
}: SectionRowsProps): JSX.Element {
  const monthCount = section.items[0]?.months.length ?? 6;

  return (
    <>
      {/* Section Header */}
      <tr className={cn(colors.bg, 'cursor-pointer hover:opacity-90 transition-opacity')} onClick={onToggle}>
        <td className={cn('sticky left-0 z-10 px-4 py-2.5', colors.bg)}>
          <div className="flex items-center gap-2">
            <div className={cn('w-1 h-5 rounded-full', colors.border.replace('border-', 'bg-'))} />
            <span className={cn('text-sm font-semibold', colors.text)}>
              {isBrazil ? section.labelBR : section.label}
            </span>
            <svg
              className={cn(
                'h-4 w-4 transition-transform',
                colors.text,
                isExpanded && 'rotate-180'
              )}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </td>
        {Array.from({ length: monthCount }).map((_, index) => {
          const total = calculateSectionTotal(section, index);
          return (
            <td key={index} className={cn('px-3 py-2.5 text-right text-sm font-semibold', colors.text)}>
              {total >= 0 ? '+' : ''}{formatCurrency(total)}
            </td>
          );
        })}
      </tr>

      {/* Section Items */}
      {isExpanded && section.items.map((item) => (
        <tr key={item.id} className="hover:bg-gray-50 transition-colors">
          <td className="sticky left-0 z-10 bg-white px-4 py-2 pl-10">
            <div className="flex items-center gap-2">
              <span className={cn(
                'text-xs',
                item.type === 'income' ? 'text-emerald-500' : 'text-rose-500'
              )}>
                {item.type === 'income' ? '(+)' : '(-)'}
              </span>
              <span className="text-sm text-gray-600">
                {isBrazil ? item.labelBR : item.label}
              </span>
            </div>
          </td>
          {item.months.map((value, index) => (
            <td
              key={index}
              className={cn(
                'px-3 py-2 text-right text-sm',
                value >= 0 ? 'text-emerald-600' : 'text-rose-600'
              )}
            >
              {formatCurrency(value)}
            </td>
          ))}
        </tr>
      ))}
    </>
  );
});

// Summary Card Component
interface SummaryCardProps {
  label: string;
  value: number;
  formatCurrency: (amount: number) => string;
  color: 'emerald' | 'rose' | 'teal' | 'violet';
  isNegative?: boolean;
  isBrazil?: boolean;
}

const colorMap = {
  emerald: { bg: 'bg-emerald-50', text: 'text-emerald-600' },
  rose: { bg: 'bg-rose-50', text: 'text-rose-600' },
  teal: { bg: 'bg-finova-teal-50', text: 'text-finova-teal-600' },
  violet: { bg: 'bg-violet-50', text: 'text-violet-600' },
};

// Compact currency formatter for mobile
const formatCompactCurrency = (amount: number, isBrazil: boolean): string => {
  const absValue = Math.abs(amount);
  if (isBrazil) {
    if (absValue >= 1000000) return `R$ ${(absValue / 1000000).toFixed(1)}M`;
    if (absValue >= 1000) return `R$ ${(absValue / 1000).toFixed(0)}K`;
    return `R$ ${absValue.toFixed(0)}`;
  }
  if (absValue >= 1000000) return `$${(absValue / 1000000).toFixed(1)}M`;
  if (absValue >= 1000) return `$${(absValue / 1000).toFixed(0)}K`;
  return `$${absValue.toFixed(0)}`;
};

const SummaryCard = memo(function SummaryCard({
  label,
  value,
  formatCurrency,
  color,
  isNegative,
  isBrazil = false,
}: SummaryCardProps): JSX.Element {
  const colors = colorMap[color];
  const fullValue = `${isNegative ? '-' : ''}${formatCurrency(value)}`;
  const compactValue = `${isNegative ? '-' : ''}${formatCompactCurrency(value, isBrazil)}`;

  return (
    <div className={cn('rounded-lg p-3 sm:p-4 overflow-hidden', colors.bg)}>
      <p className="text-[10px] sm:text-xs font-medium uppercase tracking-wide text-gray-500 truncate" title={label}>
        {label}
      </p>
      <p className={cn('mt-1 text-sm sm:text-lg font-bold truncate', colors.text)} title={fullValue}>
        <span className="sm:hidden">{compactValue}</span>
        <span className="hidden sm:inline">{fullValue}</span>
      </p>
    </div>
  );
});

export default CashFlowReport;
